import { ClipboardEvent, KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../constants';
import { completeAuthenticatedLogin, type AuthenticationData } from '../authenticatedLogin';
import './TfaEntryPage.css';

const CODE_LENGTH = 6;

const getPendingLoginName = () => {
  try {
    const pending = JSON.parse(sessionStorage.getItem('pendingAuthentication') ?? 'null');
    return typeof pending?.userName === 'string' ? pending.userName.trim() : '';
  } catch {
    return '';
  }
};

export function TfaEntryPage() {
  const [recipientEmail] = useState(() => {
    const loginName = getPendingLoginName();
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(loginName) ? loginName : '';
  });
  const [digits, setDigits] = useState<string[]>(Array(CODE_LENGTH).fill(''));
  const [resendNotice, setResendNotice] = useState('');
  const [error, setError] = useState('');
  const [checking, setChecking] = useState(false);
  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);
  const lastCheckedCode = useRef('');
  const navigate = useNavigate();

  useEffect(() => {
    const pending = sessionStorage.getItem('pendingAuthentication');
    if (!pending) navigate('/login', { replace: true });
  }, [navigate]);

  const sendAgain = () => {
    // TODO: Connect the resend API and its cooldown when the backend contract is available.
    setResendNotice('Das erneute Senden ist derzeit noch nicht verfügbar. Bitte wenden Sie sich an die Praxis.');
  };

  const check = async (code: string) => {
    const pendingValue = sessionStorage.getItem('pendingAuthentication');
    if (!pendingValue || checking) return;

    setChecking(true);
    setError('');
    try {
      const pending = JSON.parse(pendingValue) as { data: AuthenticationData; userName: string };
      const response = await fetch(`${API_BASE_URL}/verify-tfa`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: pending.data.userId, tfaCode: code }),
      });
      if (!response.ok) {
        setError('Der eingegebene Code ist ungültig. Bitte versuchen Sie es erneut.');
        lastCheckedCode.current = '';
        return;
      }

      const verificationData = await response.json() as { token?: unknown };
      if (typeof verificationData.token !== 'string' || !verificationData.token.trim()) {
        throw new Error('TFA verification response did not contain a token');
      }

      await completeAuthenticatedLogin(
        { ...pending.data, token: verificationData.token },
        pending.userName,
      );
      sessionStorage.removeItem('pendingAuthentication');
      navigate('/dashboard', { replace: true });
    } catch (verificationError) {
      console.error('TFA verification failed', verificationError);
      setError('Der Code konnte nicht geprüft werden. Bitte versuchen Sie es erneut.');
      lastCheckedCode.current = '';
    } finally {
      setChecking(false);
    }
  };

  const updateCode = (nextDigits: string[]) => {
    setDigits(nextDigits);
    const code = nextDigits.join('');

    if (code.length === CODE_LENGTH && code !== lastCheckedCode.current) {
      lastCheckedCode.current = code;
      void check(code);
    } else if (code.length < CODE_LENGTH) {
      lastCheckedCode.current = '';
    }
  };

  const enterDigits = (value: string, startIndex: number) => {
    const enteredDigits = value.replace(/\D/g, '').slice(0, CODE_LENGTH - startIndex);
    if (!enteredDigits) return;

    const nextDigits = [...digits];
    enteredDigits.split('').forEach((digit, offset) => {
      nextDigits[startIndex + offset] = digit;
    });
    updateCode(nextDigits);

    const nextIndex = Math.min(startIndex + enteredDigits.length, CODE_LENGTH - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>, index: number) => {
    event.preventDefault();
    enterDigits(event.clipboardData.getData('text'), index);
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>, index: number) => {
    if (event.key === 'Backspace' && !digits[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  return (
    <main
      className="tfa-entry-page"
      style={{ '--tfa-background-image': "url('/images/StartImage.jpg')" } as React.CSSProperties}
    >
      <section className="tfa-entry-page__card" aria-labelledby="tfa-heading">
        <button
          type="button"
          onClick={() => {
            sessionStorage.removeItem('pendingAuthentication');
            navigate('/login');
          }}
          className="tfa-entry-page__back"
          aria-label="Zurück zur Anmeldung"
        >
          <span aria-hidden="true" className="tfa-entry-page__back-arrow">
            ←
          </span>
          <span>Zurück</span>
        </button>

        <div className="tfa-entry-page__header">
          <div className="tfa-entry-page__badge">
            <svg
              aria-hidden="true"
              viewBox="0 0 24 24"
              className="tfa-entry-page__shield"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.8"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M12 3.25c2.25 1.65 4.48 2.28 6.75 2.52v5.48c0 4.3-2.78 7.72-6.75 9.5-3.97-1.78-6.75-5.2-6.75-9.5V5.77C7.52 5.53 9.75 4.9 12 3.25Z"
              />
              <path strokeLinecap="round" strokeLinejoin="round" d="m9.25 11.9 1.78 1.78 3.72-3.86" />
            </svg>
          </div>

          <h1 id="tfa-heading" className="tfa-entry-page__heading">
            Zwei-Faktor-Verifizierung
          </h1>
          <p className="tfa-entry-page__description">
            Wir haben einen 6-stelligen Code an
            <br />
            <span className="tfa-entry-page__recipient">{recipientEmail || 'Ihre E-Mail-Adresse'}</span> gesendet. Bitte geben
            Sie ihn unten ein.
          </p>
        </div>

        <div className="tfa-entry-page__digits" role="group" aria-label="6-stelliger Verifizierungscode">
          {digits.map((digit, index) => (
            <input
              key={index}
              ref={(element) => {
                inputRefs.current[index] = element;
              }}
              value={digit}
              onChange={(event) => {
                const value = event.target.value.replace(/\D/g, '');
                if (!value) {
                  const nextDigits = [...digits];
                  nextDigits[index] = '';
                  updateCode(nextDigits);
                  return;
                }
                enterDigits(value, index);
              }}
              onPaste={(event) => handlePaste(event, index)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              onFocus={(event) => event.currentTarget.select()}
              inputMode="numeric"
              autoComplete={index === 0 ? 'one-time-code' : 'off'}
              maxLength={CODE_LENGTH}
              aria-label={`Ziffer ${index + 1}`}
              className="tfa-entry-page__digit"
              disabled={checking}
            />
          ))}
        </div>
        {error && <p className="tfa-entry-page__notice" role="alert">{error}</p>}

        <aside className="tfa-entry-page__help" aria-labelledby="tfa-help-heading">
          <h2 id="tfa-help-heading" className="tfa-entry-page__help-heading">
            Keinen Code erhalten?
          </h2>
          <p className="tfa-entry-page__help-description">
            Prüfen Sie den Spam-Ordner und kontrollieren Sie, ob die E-Mail-Adresse korrekt ist.
          </p>
          <button type="button" onClick={sendAgain} className="tfa-entry-page__resend">
            Code erneut senden
          </button>
          <p className="tfa-entry-page__notice" role="status">
            {resendNotice}
          </p>
        </aside>
      </section>
    </main>
  );
}
